import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Clock, ArrowLeft, Plus, Calendar, PenLine } from "lucide-react";
import { blogArticles, getArticleBySlug, resolveArticle } from "../data/blogArticles";
import { useLanguage } from "../LanguageContext";
import { usePageMeta } from "../hooks/usePageMeta";

// =============================================================
// Blog Index — list of all articles
// =============================================================
export const BlogIndexContent = ({ onOpenModal }) => {
  const { language, t } = useLanguage();
  const isEn = language === "en";

  usePageMeta({
    title: isEn
      ? "Blog — Brice Médium | Messages from Beyond"
      : "Blog — Brice Médium | Messages de l'Au-delà",
    description: isEn
      ? "Discover the secrets of communication with the deceased. Signs, dreams, messages and authentic testimonies. First 10 minutes free."
      : "Découvrez les secrets de la communication avec les défunts. Signes, rêves, messages et témoignages authentiques. 10 premières minutes gratuites.",
    url: "https://bricemedium.fr/blog",
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [isEn]);

  return (
    <div className="bg-dark-bg text-cream min-h-screen">
      {/* Hero */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-24 px-4 sm:px-6 overflow-hidden">
        {/* Glow */}
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, rgba(201,169,110,0.08) 0%, transparent 70%)",
          }}
        />
        <div className="relative max-w-4xl mx-auto text-center">
          <div
            className="inline-flex items-center gap-2 px-5 py-2 mb-8 rounded-full border border-sepia/30 bg-sepia/10 backdrop-blur-sm"
            data-testid="blog-hero-badge"
          >
            <span className="w-1.5 h-1.5 bg-sepia rounded-full animate-pulse" />
            <span className="text-xs tracking-[0.2em] uppercase text-sepia font-lato">
              {t('blog.heroBadge')}
            </span>
          </div>
          <h1 className="font-cormorant text-4xl sm:text-5xl md:text-6xl font-light text-cream mb-6 leading-tight">
            {t('blog.heroTitle1')}{" "}
            <em className="text-sepia italic font-normal">{t('blog.heroTitleEm')}</em>
          </h1>
          {/* Mobile - with custom line breaks */}
          <p className="md:hidden font-cormorant text-lg text-white max-w-2xl mx-auto leading-relaxed whitespace-pre-line">
            {t('blog.heroSubtitleMobile')}
          </p>
          {/* Desktop */}
          <p className="hidden md:block font-cormorant text-xl text-cream/80 max-w-2xl mx-auto leading-relaxed">
            {t('blog.heroSubtitleDesktop')}
          </p>
        </div>
      </section>

      {/* Articles */}
      <section className="py-12 md:py-16 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            {/* Mobile h2 — "à la une" in sepia */}
            <h2 className="md:hidden font-playfair text-3xl text-white mb-3 font-light">
              {t('blog.sectionTitle')} <span className="text-sepia italic">{t('blog.sectionTitleEm')}</span>
            </h2>
            {/* Desktop h2 */}
            <h2 className="hidden md:block font-playfair text-4xl text-cream mb-3 font-light">
              {t('blog.sectionTitle')} {t('blog.sectionTitleEm')}
            </h2>
            {/* Mobile - with line break, in white */}
            <p className="md:hidden font-cormorant text-lg text-white italic whitespace-pre-line">
              {t('blog.sectionSubtitleMobile')}
            </p>
            {/* Desktop */}
            <p className="hidden md:block font-cormorant text-xl text-cream/70 italic">
              {t('blog.sectionSubtitleDesktop')}
            </p>
            <div className="w-16 h-px bg-sepia mx-auto mt-6 opacity-50" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {blogArticles.map((article) => {
              const a = resolveArticle(article, language);
              return (
              <Link
                key={article.slug}
                to={`/blog/${article.slug}`}
                className="group block bg-dark-card border-2 border-sepia/60 md:border md:border-gold-muted/15 hover:border-sepia/40 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(0,0,0,0.3),0_0_60px_rgba(201,169,110,0.08)] overflow-hidden"
                data-testid={`blog-card-${article.slug}`}
              >
                {/* Cover image (with emoji fallback) */}
                <div
                  className="h-44 flex items-center justify-center border-b border-gold-muted/10 text-6xl overflow-hidden relative"
                  style={{
                    background:
                      "linear-gradient(135deg, #0f0f18 0%, #1a1a2e 100%)",
                  }}
                >
                  {a.coverImage ? (
                    <>
                      <img
                        src={a.coverImage}
                        alt={a.coverAlt || a.title}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-dark-card via-dark-card/40 to-transparent" />
                    </>
                  ) : (
                    <span aria-hidden="true">{a.emoji}</span>
                  )}
                </div>

                <div className="p-6 md:p-7">
                  <span className="inline-block px-3 py-1 mb-3 text-xs tracking-[0.15em] uppercase text-sepia bg-sepia/10 rounded-full font-lato">
                    {a.tag}
                  </span>
                  {/* Mobile: title in sepia; Desktop: cream */}
                  <h3 className="font-cormorant text-2xl md:text-3xl text-sepia md:text-cream mb-3 leading-tight font-medium group-hover:text-sepia transition-colors">
                    {a.title}
                  </h3>
                  {/* Mobile: excerpt in white; Desktop: cream/70 */}
                  <p className="font-cormorant text-lg text-white md:text-cream/70 mb-5 leading-relaxed">
                    {a.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-sm text-cream/50 font-lato">
                    <span className="flex items-center gap-1.5">
                      <Clock size={14} />
                      {a.readTime}
                    </span>
                    <span className="text-sepia group-hover:translate-x-1 transition-transform">
                      <ArrowRight size={18} />
                    </span>
                  </div>
                </div>
              </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 px-4 sm:px-6 relative">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, transparent 0%, rgba(15,15,24,0.6) 50%, transparent 100%)",
          }}
        />
        <div className="relative max-w-2xl mx-auto text-center">
          <div className="text-4xl mb-6" aria-hidden="true">🕊️</div>
          <h2 className="font-cormorant text-4xl md:text-5xl text-cream mb-4 font-light">
            {t('blog.ctaTitle1')}{" "}
            <em className="text-sepia italic font-normal">{t('blog.ctaTitleEm')}</em>{" "}
            {t('blog.ctaTitle2')}
          </h2>
          <p className="font-cormorant text-lg md:text-xl text-white mb-8 leading-relaxed">
            {t('blog.ctaSubtitle')}
          </p>
          <button
            onClick={onOpenModal}
            className="inline-flex items-center gap-3 px-8 py-4 border border-sepia/40 text-sepia hover:bg-sepia/10 hover:border-sepia transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_10px_30px_rgba(201,169,110,0.15)] font-lato text-sm tracking-[0.15em] uppercase"
            data-testid="blog-cta-btn"
          >
            {t('blog.ctaButton')}
            <ArrowRight size={18} />
          </button>
        </div>
      </section>
    </div>
  );
};

// =============================================================
// Single Article view
// =============================================================
export const BlogArticleContent = ({ onOpenModal }) => {
  const { slug } = useParams();
  const { language, t } = useLanguage();
  const rawArticle = getArticleBySlug(slug);
  const article = resolveArticle(rawArticle, language);
  const contentRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [openFaq, setOpenFaq] = useState(null);

  usePageMeta(
    article
      ? {
          title: `${article.title} | Brice Médium`,
          description: article.metaDescription || article.excerpt,
          url: `https://bricemedium.fr/blog/${slug}`,
        }
      : {}
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    // Add meta keywords if provided
    if (article && article.metaKeywords) {
      let metaKw = document.querySelector('meta[name="keywords"]');
      if (!metaKw) {
        metaKw = document.createElement("meta");
        metaKw.setAttribute("name", "keywords");
        document.head.appendChild(metaKw);
      }
      metaKw.setAttribute("content", article.metaKeywords);
    }
  }, [article]);

  // Reading progress bar
  useEffect(() => {
    const onScroll = () => {
      const winScroll =
        document.body.scrollTop || document.documentElement.scrollTop;
      const height =
        document.documentElement.scrollHeight -
        document.documentElement.clientHeight;
      const pct = height > 0 ? (winScroll / height) * 100 : 0;
      setProgress(pct);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [slug]);

  // Inline CTA buttons inside dangerouslySetInnerHTML — bind click via delegation
  useEffect(() => {
    const node = contentRef.current;
    if (!node) return;
    const handler = (e) => {
      const target = e.target.closest(".cta-inline-btn, [data-cta='open-modal']");
      if (target) {
        e.preventDefault();
        onOpenModal && onOpenModal();
      }
    };
    node.addEventListener("click", handler);
    return () => node.removeEventListener("click", handler);
  }, [onOpenModal, slug, article]);

  // 404-style fallback
  if (!article) {
    return (
      <div className="bg-dark-bg text-cream min-h-screen flex flex-col items-center justify-center px-4 text-center">
        <h1 className="font-cormorant text-4xl md:text-5xl text-sepia mb-4">
          {t('blog.notFoundTitle')}
        </h1>
        <p className="font-cormorant text-lg text-cream/70 mb-8 max-w-md">
          {t('blog.notFoundDesc')}
        </p>
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 px-6 py-3 border border-sepia/40 text-sepia hover:bg-sepia/10 transition-all font-lato text-sm tracking-wider uppercase"
        >
          <ArrowLeft size={16} />
          {t('blog.backToBlog')}
        </Link>
      </div>
    );
  }

  const hasContent = article.content && article.content.trim().length > 0;
  const faqs = Array.isArray(article.faqs) ? article.faqs : [];

  return (
    <div className="bg-dark-bg text-cream min-h-screen">
      {/* Reading progress bar */}
      <div
        className="fixed top-0 left-0 h-[2px] bg-sepia z-[200] transition-[width] duration-100"
        style={{ width: `${progress}%` }}
        aria-hidden="true"
      />

      {/* Breadcrumb */}
      <nav
        className="pt-28 md:pt-32 px-4 sm:px-6 max-w-3xl mx-auto text-xs sm:text-sm text-cream/50 font-lato"
        aria-label="Fil d'Ariane"
      >
        <Link to="/" className="hover:text-sepia transition-colors">
          {t('blog.breadcrumbHome')}
        </Link>
        <span className="mx-2 text-cream/30">›</span>
        <Link to="/blog" className="hover:text-sepia transition-colors">
          {t('blog.breadcrumbBlog')}
        </Link>
        <span className="mx-2 text-cream/30">›</span>
        <span className="text-cream/70">{article.breadcrumb || article.title}</span>
      </nav>

      {/* Header zone */}
      <section className="relative pt-6 pb-10 md:pt-10 md:pb-14 px-4 sm:px-6 overflow-hidden">
        <div
          className="absolute -top-20 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, rgba(201,169,110,0.07) 0%, transparent 70%)",
          }}
        />
        <div className="relative max-w-3xl mx-auto">
          <span className="inline-block px-3 py-1 mb-5 text-xs tracking-[0.15em] uppercase text-sepia bg-sepia/10 rounded-full font-lato">
            {article.tag}
          </span>
          <h1 className="font-cormorant text-3xl sm:text-4xl md:text-5xl text-cream mb-6 leading-tight font-light">
            {article.title}
          </h1>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-cream/55 font-lato">
            <span className="flex items-center gap-1.5">
              <Clock size={14} />
              {article.readTime}
            </span>
            <span className="hidden sm:inline w-px h-3 bg-cream/20" />
            <span className="flex items-center gap-1.5">
              <PenLine size={14} />
              {t('blog.authorName')}
            </span>
            {article.publishedDateLabel && (
              <>
                <span className="hidden sm:inline w-px h-3 bg-cream/20" />
                <span className="flex items-center gap-1.5">
                  <Calendar size={14} />
                  {article.publishedDateLabel}
                </span>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Article body */}
      <article className="px-4 sm:px-6 pb-16">
        <div className="max-w-3xl mx-auto">
          {/* Cover image header (with emoji fallback) */}
          <div
            className="h-56 md:h-72 flex items-center justify-center border border-gold-muted/15 mb-10 text-7xl md:text-8xl overflow-hidden relative"
            style={{
              background:
                "linear-gradient(135deg, #0f0f18 0%, #1a1a2e 100%)",
            }}
          >
            {article.coverImage ? (
              <>
                <img
                  src={article.coverImage}
                  alt={article.coverAlt || article.title}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-dark-bg via-dark-bg/30 to-dark-bg/10" />
              </>
            ) : (
              <span aria-hidden="true">{article.emoji}</span>
            )}
          </div>

          {hasContent ? (
            <div
              ref={contentRef}
              className="blog-article-content font-cormorant text-lg text-cream/85 leading-[1.85]"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          ) : (
            <div className="text-center py-12 border border-gold-muted/20 bg-dark-card/50">
              <p className="font-cormorant text-xl md:text-2xl text-sepia italic mb-4">
                {t('blog.writingPlaceholder')}
              </p>
              <p className="font-cormorant text-base text-cream/65 max-w-md mx-auto px-6">
                {article.excerpt}
              </p>
              <p className="font-lato text-xs tracking-[0.2em] uppercase text-cream/40 mt-6">
                {t('blog.writingHint')}
              </p>
            </div>
          )}

          {/* FAQ accordion */}
          {faqs.length > 0 && (
            <section className="mt-16 mb-4" id="faq" data-testid="faq-section">
              <h2 className="font-cormorant text-3xl md:text-4xl text-cream font-light text-center mb-8">
                {t('blog.faqTitle')} <em className="text-sepia italic font-normal">{t('blog.faqTitleEm')}</em>
              </h2>
              <div className="space-y-3">
                {faqs.map((faq, idx) => {
                  const isOpen = openFaq === idx;
                  return (
                    <div
                      key={idx}
                      className={`bg-dark-card border ${
                        isOpen ? "border-sepia/40" : "border-gold-muted/15"
                      } overflow-hidden transition-colors`}
                      data-testid={`faq-item-${idx}`}
                    >
                      <button
                        type="button"
                        onClick={() => setOpenFaq(isOpen ? null : idx)}
                        className="w-full text-left flex items-center justify-between gap-4 px-5 md:px-6 py-4 md:py-5 hover:bg-sepia/5 transition-colors"
                      >
                        <span className="font-cormorant text-lg md:text-xl text-cream pr-4 leading-snug">
                          {faq.question}
                        </span>
                        <Plus
                          size={20}
                          className={`text-sepia shrink-0 transition-transform duration-300 ${
                            isOpen ? "rotate-45" : ""
                          }`}
                        />
                      </button>
                      <div
                        className={`grid transition-all duration-400 ease-in-out ${
                          isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                        }`}
                      >
                        <div className="overflow-hidden">
                          <p className="px-5 md:px-6 pb-5 md:pb-6 font-cormorant text-base md:text-lg text-cream/75 leading-relaxed">
                            {faq.answer}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </div>
      </article>

      {/* CTA */}
      <section className="py-16 md:py-20 px-4 sm:px-6 relative">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, transparent 0%, rgba(15,15,24,0.6) 50%, transparent 100%)",
          }}
        />
        <div className="relative max-w-2xl mx-auto text-center">
          <div className="text-4xl mb-6" aria-hidden="true">🕊️</div>
          <h2 className="font-cormorant text-4xl md:text-5xl text-cream mb-4 font-light">
            {t('blog.ctaTitle1')}{" "}
            <em className="text-sepia italic font-normal">{t('blog.ctaTitleEm')}</em>{" "}
            {t('blog.ctaTitle2')}
          </h2>
          <p className="font-cormorant text-lg md:text-xl text-white mb-8 leading-relaxed">
            {t('blog.ctaSubtitle')}
          </p>
          <button
            onClick={onOpenModal}
            className="inline-flex items-center gap-3 px-8 py-4 border border-sepia/40 text-sepia hover:bg-sepia/10 hover:border-sepia transition-all duration-300 hover:-translate-y-0.5 font-lato text-sm tracking-[0.15em] uppercase"
            data-testid="article-cta-btn"
          >
            {t('blog.ctaButton')}
            <ArrowRight size={18} />
          </button>
        </div>
      </section>

      {/* Related articles */}
      <section className="pb-20 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h3 className="font-playfair text-2xl md:text-3xl text-cream font-light">
              {t('blog.relatedTitle')}
            </h3>
            <div className="w-12 h-px bg-sepia mx-auto mt-4 opacity-50" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {blogArticles
              .filter((a) => a.slug !== article.slug)
              .slice(0, 3)
              .map((rawA) => {
                const a = resolveArticle(rawA, language);
                return (
                <Link
                  key={a.slug}
                  to={`/blog/${a.slug}`}
                  className="group block bg-dark-card border border-gold-muted/15 hover:border-sepia/40 transition-all duration-500 hover:-translate-y-1 p-5"
                  data-testid={`related-article-${a.slug}`}
                >
                  <div className="text-3xl mb-3" aria-hidden="true">
                    {a.emoji}
                  </div>
                  <h4 className="font-cormorant text-lg text-cream group-hover:text-sepia transition-colors leading-snug">
                    {a.title}
                  </h4>
                </Link>
                );
              })}
          </div>
        </div>
      </section>
    </div>
  );
};
